import classNames from 'classnames';
import * as React from 'react';

import * as styles from './FavoriteEntity.css';
import { FavoriteEntityControlsBarContainer } from '../../containers/FavoriteEntityControlsBar';
import { MainImageContainer } from '../../containers/MainImage';
import { IApplicationContext } from '../../types/applicationContext';
import { IFavoriteEntity } from '../../types/results';
import { ApplicationContext } from '../../utils/applicationContext';
import { getIsOfferWithMultiAds } from '../../utils/getIsOfferWithMultiAds';
import { Address } from '../Address';
import { Agent } from '../Agent';
import { AreaParts } from '../AreaParts';
import { AreaPartsMultiAds } from '../AreaPartsMultiAds';
import { CardShields } from '../CardShields';
import { ClientComment } from '../ClientComment';
import { CommentSection } from '../CommentSection';
import { Description } from '../Description';
import { FavoriteInfo } from '../FavoriteInfo';
import { Undergrounds } from '../Undergrounds';

export interface IFavoriteEntityOwnProps {
  favoriteEntity: IFavoriteEntity;
  position: number;
  isEditComment: boolean;
  isFetchingComment: boolean;
  isHideForPrint: boolean;
  printComments: boolean;
  isSearcherOrSub: boolean;
  checkbox: React.ReactNode | null;
}

export type TFavoriteEntityProps = IFavoriteEntityOwnProps;

export const ONE_LINE_HEIGHT = 20;

export class FavoriteEntity extends React.Component<TFavoriteEntityProps> {
  private htmlDescription: HTMLDivElement | null = null;
  public static contextType?: React.Context<IApplicationContext> | undefined = ApplicationContext;

  public componentDidMount() {
    /**
     * Убираем градиент у однострочных и двустрочных описание.
     * На третьей строке отображаем градиент всегда.
     */
    if (this.htmlDescription) {
      const { scrollHeight } = this.htmlDescription;

      if (scrollHeight === ONE_LINE_HEIGHT || scrollHeight === ONE_LINE_HEIGHT * 2) {
        this.htmlDescription.classList.add(styles['description--no-gradient']);
      }
    }
  }

  public render() {
    const {
      favoriteEntity,
      isHideForPrint,
      printComments,
      isEditComment,
      isFetchingComment,
      checkbox,
      isSearcherOrSub,
      position,
    } = this.props;
    const {
      entityId,
      note,
      geo,
      agentInfo,
      clientReactions: { comment },
      commercialOwnership,
      isBookedFromDeveloper,
    } = favoriteEntity;

    const { config } = this.context as IApplicationContext;

    /**
     * @todo Удалить переменную когда будет удален фича-тогл
     * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192014
     */
    const shouldShowShieldsUnderTitle = config.get<Boolean>('Favorites.ElectronicTrading.Enabled');

    const isBookedFromDeveloperEnabled = config.get<Boolean>('Newbuilding.BookedFromDeveloperDesktop.Enabled');
    const isBooked = Boolean(isBookedFromDeveloperEnabled && isBookedFromDeveloper);

    return (
      <div className={classNames(styles['container'], { [styles['hide_print']]: isHideForPrint })}>
        <div className={styles['row']}>
          {checkbox && <div className={styles['checkbox']}>{checkbox}</div>}
          <div className={styles['image']}>
            <MainImageContainer favoriteEntity={favoriteEntity} />
            {/*
             * @todo Удалить блок с шильдиками и класс shields_wrapper--legacy когда будет удален фича-тогл
             * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192014
             */}
            {!shouldShowShieldsUnderTitle && (
              <div data-testid="card-shields" className={styles['shields_wrapper--legacy']}>
                <CardShields
                  agentLists={agentInfo?.agentLists}
                  isCommericalOwner={!!commercialOwnership?.isFromOwner}
                />
              </div>
            )}
          </div>
          <div className={styles['main_content']}>
            <div className={styles['info']}>
              <div>
                <div className={styles['info_top']}>
                  <FavoriteInfo favoriteEntity={favoriteEntity} isBooked={isBooked} />
                </div>
                {/*
                 * @todo Удалить условие shouldShowShieldsUnderTitle когда будет удален фича-тогл
                 * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192014
                 */}
                {shouldShowShieldsUnderTitle && (
                  <div data-testid="card-shields" className={styles['shields_wrapper']}>
                    <CardShields
                      agentLists={agentInfo?.agentLists}
                      isCommericalOwner={!!commercialOwnership?.isFromOwner}
                    />
                  </div>
                )}
                <div className={styles['geo_info']}>
                  {geo && <Undergrounds undergrounds={geo.undergrounds} />}
                  {geo && <Address address={geo.address} isBooked={isBooked} />}
                </div>
                {this.renderDescription()}
                {this.renderCommercialAreaParts()}
              </div>

              <FavoriteEntityControlsBarContainer
                isSearcherOrSub={isSearcherOrSub}
                favoriteEntity={favoriteEntity}
                position={position}
                isBooked={isBooked}
              />
            </div>
            <div className={styles['agent']}>{agentInfo && <Agent agentInfo={agentInfo} isBooked={isBooked} />}</div>
          </div>
        </div>

        <div className={classNames(styles['row'], styles['comment'], !printComments && styles['hide_print'])}>
          {(note || isEditComment) && (
            <CommentSection
              editable={isEditComment}
              comment={note}
              entityId={entityId}
              isFetching={isFetchingComment}
              name={agentInfo?.name || ''}
            />
          )}
        </div>
        {comment && (
          <div className={classNames(styles['row'], styles['comment'], !printComments && styles['hide_print'])}>
            <ClientComment title={isSearcherOrSub ? 'Комментарий' : 'Комментарий клиента'} text={comment} />
          </div>
        )}
      </div>
    );
  }

  private renderDescription = () => {
    const { favoriteEntity } = this.props;
    const { description, commercialInfo, isBookedFromDeveloper } = favoriteEntity;
    const areaParts = commercialInfo?.areaParts;
    const isMultiAds = getIsOfferWithMultiAds(commercialInfo);

    if (isMultiAds) {
      return null;
    }

    return (
      <div
        className={classNames(styles['description'], { [styles['description-with-areaParts']]: areaParts?.length })}
        ref={div => (this.htmlDescription = div)}
      >
        {description && <Description description={description} isBooked={Boolean(isBookedFromDeveloper)} />}
      </div>
    );
  };

  private renderCommercialAreaParts = () => {
    const { favoriteEntity } = this.props;
    const { entityId, fullUrl, commercialInfo } = favoriteEntity;
    const areaParts = commercialInfo?.areaParts;
    const isMultiAds = areaParts && areaParts.length > 0;

    if (isMultiAds) {
      return <AreaPartsMultiAds areaParts={areaParts} fullUrl={fullUrl} />;
    }

    return isMultiAds && <AreaParts areaParts={areaParts} entityId={entityId} fullUrl={fullUrl} />;
  };
}
